import { Component, computed, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FilterOption, TableFilterState } from 'shared';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';
import { TableFilterDropdownComponent } from '../../molecules/table-filter-dropdown/table-filter-dropdown.component';
import { TableFilterMobilePanelComponent } from '../table-filter-mobile-panel/table-filter-mobile-panel.component';

/**
 * Search box + per-field filter UI for a table, driven by a `TableFilterState`, so each table
 * (Tasks, Models, Members...) can share this instead of reimplementing the desktop filter bar /
 * mobile icons+chips+panel combo. Renders a row of `ocp-table-filter-dropdown`s on desktop, or a
 * search/funnel icon row + active filter chips + `ocp-table-filter-mobile-panel` on mobile.
 */
@Component({
  selector: 'ocp-table-filter-toolbar',
  imports: [
    TranslatePipe,
    ThemedIconComponent,
    TableFilterDropdownComponent,
    TableFilterMobilePanelComponent,
  ],
  templateUrl: './table-filter-toolbar.component.html',
  styleUrl: './table-filter-toolbar.component.scss',
  host: {
    class: 'table-filter-toolbar',
  },
})
export class TableFilterToolbarComponent<TItem = unknown> {
  private static nextId = 0;

  readonly filterState = input.required<TableFilterState<TItem>>();
  readonly searchQuery = input.required<string>();
  readonly isMobile = input.required<boolean>();

  /** Used as the search input's placeholder, visually-hidden label, and icon button aria-label. */
  readonly searchAriaLabel = input.required<string>();
  readonly filterAriaLabel = input.required<string>();
  readonly filterRemoveAriaLabel = input.required<string>();
  readonly showAllLabel = input.required<string>();
  readonly clearAllLabel = input.required<string>();
  readonly panelTitle = input.required<string>();
  readonly applyLabel = input.required<string>();
  readonly selectLabel = input.required<string>();
  readonly closeAriaLabel = input.required<string>();
  readonly backAriaLabel = input.required<string>();
  readonly selectedCountLabel = input.required<(count: number) => string>();

  readonly searchQueryChange = output<string>();

  protected readonly searchInputId = `table-filter-toolbar-search-${TableFilterToolbarComponent.nextId++}`;
  protected readonly mobileSearchOpen = signal(false);
  protected readonly mobileFiltersOpen = signal(false);

  /** Total number of selected values across all filter fields, shown as the funnel icon's badge. */
  protected readonly activeFilterValueCount = computed(() =>
    this.filterState().fields.reduce(
      (total, field) => total + this.filterState().getSelectedCount(field.key)(),
      0
    )
  );

  protected onSearchInput(event: Event): void {
    this.searchQueryChange.emit((event.target as HTMLInputElement).value);
  }

  protected onMobileSearchToggle(): void {
    this.mobileSearchOpen.update(open => !open);
  }

  /** Currently selected options for a non-subpage field, for the mobile active-filter chip row. */
  protected getSelectedFilterOptions(fieldKey: string): FilterOption[] {
    const selected = this.filterState().getSelectedValues(fieldKey)();
    return this.filterState()
      .getFieldOptions(fieldKey)()
      .filter(option => selected.has(String(option.value)));
  }
}
